import { ExpandMoreOutlined } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { Box, Button, Fade, Skeleton, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { assetService } from "../../../Core/Api/services";
import {
  AccessGroupInfos,
  AccessUserInfos,
  PermissionByMode,
  PermissionCombination,
  UsersAndGroupsByPermissionCombination,
} from "../../../Core/Api/Types/access-models";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { PermissionSubjectType, SpecificPermission } from "../../../Core/Api/Types/permission-infos";
import { PermissionMode } from "../../../Core/Api/Types/permission-mode";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { Distinct, UserHasAcl } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import { ModalContext } from "../../Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import ModalCreateOrUpdatePerms, {
  ModalCreateOrUpdatePermsElementProps,
} from "../../Modals/ModalCreateOrUpdatePerms/modal-create-or-update-perms";
import ModalUpdateDefaultPermissions, {
  ModalUpdateDefaultPermissionsElementProps,
} from "../../Modals/ModalUpdateDefaultPermissions/modal-update-default-permissions";
import AccessAccordion from "../Shared/access-accordion";
import AccessAccordionDetails from "../Shared/access-accordion-details";
import AccessAccordionSummary from "../Shared/access-accordion-summary";
import UserAccessCard from "./user-access-card";

interface UserAccessAccordionProps {
  asset: DbAssetObjectInfos;
  allUserInfos: AccessUserInfos[];
  allGroupInfos: AccessGroupInfos[];
  isParentLoading: boolean;
}

const UserAccessAccordion = (props: UserAccessAccordionProps) => {
  const { t } = useTranslation();
  const { asset, allUserInfos, allGroupInfos, isParentLoading } = props;

  // Imports
  const enqueueSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Acls
  const userCanSetPermissions = UserHasAcl(AclTypes.aclPermissionSet);
  const userCanDeletePermissions = UserHasAcl(AclTypes.aclPermissionDelete);

  // States
  const [_isLoading, set_IsLoading] = React.useState(true);
  const [assetSpecificPermissions, setAssetSpecificPermissions] = React.useState<SpecificPermission[]>([]);
  const [areSpecificPermissionsFromParent, setAreSpecificPermissionsFromParent] = React.useState(false);
  const [assetDefaultPermissions, setAssetDefaultPermissions] = React.useState<PermissionType[] | null>(null);
  const [areDefaultPermissionsFromParent, setAreDefaultPermissionsFromParent] = React.useState(false);
  const [isChildrenHovered, setIsChildrenHovered] = React.useState(false);
  const { active: permissionsExpanded, toggle: togglePermissionsExpanded } = useToggle(false);

  // Memos
  // Specific
  const isLoading = React.useMemo(() => {
    return isParentLoading || _isLoading;
  }, [isParentLoading, _isLoading]);

  const usersAndGroupsByPermissionCombination = React.useMemo(() => {
    if (!allUserInfos || !allGroupInfos || !assetSpecificPermissions.length) return [];

    return GetComputedUsersAndGroupsByPermissionCombination(assetSpecificPermissions, allUserInfos, allGroupInfos);
  }, [assetSpecificPermissions, allUserInfos, allGroupInfos]);

  const countUserAccesses = React.useMemo(() => {
    return usersAndGroupsByPermissionCombination.reduce(
      (acc, item) => acc + item.users.length + item.groups.reduce((gAcc, gItem) => gAcc + gItem.count, 0),
      0
    );
  }, [usersAndGroupsByPermissionCombination]);

  const availableUserInfos = React.useMemo(() => {
    if (areSpecificPermissionsFromParent) return allUserInfos;

    return allUserInfos.filter(
      (user) => !usersAndGroupsByPermissionCombination.some((item) => item.users.includes(user))
    );
  }, [allUserInfos, usersAndGroupsByPermissionCombination, areSpecificPermissionsFromParent]);

  const availableGroupInfos = React.useMemo(() => {
    if (areSpecificPermissionsFromParent) return allGroupInfos;

    return allGroupInfos.filter(
      (group) => !usersAndGroupsByPermissionCombination.some((item) => item.groups.includes(group))
    );
  }, [allGroupInfos, usersAndGroupsByPermissionCombination, areSpecificPermissionsFromParent]);

  // Default
  const defaultUsersCombination: PermissionCombination = React.useMemo(() => {
    return (Object.keys(PermissionType) as Array<PermissionType>).sort().map((permission) => ({
      permission,
      mode: assetDefaultPermissions?.includes(permission) ? PermissionMode.Allow : PermissionMode.Deny,
    }));
  }, [assetDefaultPermissions]);

  // Handlers
  const handleCreateClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();

    openModal &&
      openModal<ModalCreateOrUpdatePermsElementProps>(ModalCreateOrUpdatePerms, {
        asset: asset!,
        availableUserInfos,
        availableGroupInfos,
        onSuccess: loadAssetPermissions,
      });
  };

  const handleSpecificEditClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    combination: UsersAndGroupsByPermissionCombination
  ) => {
    event.stopPropagation();

    if (areSpecificPermissionsFromParent) return;

    openModal &&
      openModal<ModalCreateOrUpdatePermsElementProps>(ModalCreateOrUpdatePerms, {
        asset: asset!,
        availableUserInfos,
        availableGroupInfos,
        updateCombination: combination,
        onSuccess: loadAssetPermissions,
      });
  };

  const onConfirmSpecificDelete = (combination: UsersAndGroupsByPermissionCombination) => {
    const userIds = combination.users.map((user) => user.id);
    const groupIds = combination.groups.map((group) => group.id);

    assetService.DeleteSpecificPermissions({ objectId: asset.id, userIds, groupIds }).then((response) => {
      if (!response.success) {
        enqueueSnackbar(response.message || t("alert:error.oopsSomethingWentWrongWithDot"), "error");
      } else {
        loadAssetPermissions();
      }
    });
  };

  const handleSpecificDeleteClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    combination: UsersAndGroupsByPermissionCombination
  ) => {
    event.stopPropagation();

    if (areSpecificPermissionsFromParent) return;

    const count = combination.users.length + combination.groups.reduce((acc, group) => acc + group.count, 0);
    openModal &&
      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        title: t("modal:title.deletePermissionOfUsers", { count }),
        canConfirmWithoutComment: true,
        onConfirm: () => onConfirmSpecificDelete(combination),
      });
  };

  const handleDefaultEditClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();

    openModal &&
      openModal<ModalUpdateDefaultPermissionsElementProps>(ModalUpdateDefaultPermissions, {
        asset,
        selectedPermissions: assetDefaultPermissions || [],
        onSuccess: loadAssetPermissions,
      });
  };

  const onConfirmDefaultDelete = () => {
    assetService.SetDefaultPermissions({ objectId: asset.id, defaultPermissions: null }).then((response) => {
      if (!response.success) {
        enqueueSnackbar(response.message || t("alert:error.oopsSomethingWentWrongWithDot"), "error");
      } else {
        loadAssetPermissions();
      }
    });
  };

  const handleDefaultDeleteClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();

    if (areSpecificPermissionsFromParent) return;

    openModal &&
      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        title: t("modal:title.deleteDefaultPermissionForAllPlatformUsers"),
        canConfirmWithoutComment: true,
        onConfirm: () => onConfirmDefaultDelete(),
      });
  };

  const loadAssetPermissions = () => {
    assetService
      .GetPermissionsByObjectId(asset.id)
      .then((response) => {
        if (!response.success)
          enqueueSnackbar(response.message || t("alert:error.oopsSomethingWentWrongWithDot"), "error");
        else {
          setAssetSpecificPermissions(response.specificPermissionsDto?.permissions || []);
          setAreSpecificPermissionsFromParent(response.specificPermissionsDto?.areFromParent || false);

          setAssetDefaultPermissions(response.defaultPermissionsDto?.permissions || null);
          setAreDefaultPermissionsFromParent(response.defaultPermissionsDto?.areFromParent || false);
        }
      })
      .finally(() => set_IsLoading(false));
  };

  React.useEffect(() => {
    loadAssetPermissions();
  }, [asset]);

  return (
    <>
      {!isLoading ? (
        <AccessAccordion expanded={permissionsExpanded} isChildrenHovered={isChildrenHovered}>
          <AccessAccordionSummary
            expandIcon={<ExpandMoreOutlined color="inherit" />}
            onClick={togglePermissionsExpanded}
          >
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Stack direction="row" pl={0.5} spacing={1} alignItems="center">
                <PersonOutlinedIcon color="accent" />
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">{t("page:body.userAccess")}</Typography>
                  <Typography variant="body1" color="text.tier">
                    {t("common:userCount", {
                      count: countUserAccesses,
                    })}
                  </Typography>
                </Stack>
              </Stack>
              <Box>
                {userCanSetPermissions && (
                  <Fade in={permissionsExpanded}>
                    <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={handleCreateClick}>
                      {t("common:create")}
                    </Button>
                  </Fade>
                )}
              </Box>
            </Stack>
          </AccessAccordionSummary>
          <AccessAccordionDetails>
            <Stack spacing={1.5}>
              {assetDefaultPermissions && (
                <UserAccessCard
                  isAllUsers={true}
                  permissionCombination={defaultUsersCombination}
                  isFromParent={areDefaultPermissionsFromParent}
                  userCanSetPermissions={userCanSetPermissions}
                  userCanDeletePermissions={userCanDeletePermissions && !areDefaultPermissionsFromParent}
                  onEdit={handleDefaultEditClick}
                  onDelete={handleDefaultDeleteClick}
                  onMouseEnterCard={() => setIsChildrenHovered(true)}
                  onMouseLeaveCard={() => setIsChildrenHovered(false)}
                />
              )}
              {usersAndGroupsByPermissionCombination.map((item, index) => (
                <UserAccessCard
                  key={index}
                  isAllUsers={false}
                  {...item}
                  isFromParent={areSpecificPermissionsFromParent}
                  userCanSetPermissions={userCanSetPermissions && !areSpecificPermissionsFromParent}
                  userCanDeletePermissions={userCanDeletePermissions && !areSpecificPermissionsFromParent}
                  onEdit={(e) => handleSpecificEditClick(e, item)}
                  onDelete={(e) => handleSpecificDeleteClick(e, item)}
                  onMouseEnterCard={() => setIsChildrenHovered(true)}
                  onMouseLeaveCard={() => setIsChildrenHovered(false)}
                />
              ))}
            </Stack>
          </AccessAccordionDetails>
        </AccessAccordion>
      ) : (
        <Skeleton variant="rounded" height={64} />
      )}
    </>
  );
};

export default UserAccessAccordion;

// #region types, interfaces and functions

function CombinationEquals(a: PermissionCombination, b: PermissionCombination): boolean {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (a[i].permission !== b[i].permission || a[i].mode !== b[i].mode) return false;
  }

  return true;
}

function GetComputedUsersAndGroupsByPermissionCombination(
  assetPerms: SpecificPermission[],
  userInfos: AccessUserInfos[],
  groupInfos: AccessGroupInfos[]
): UsersAndGroupsByPermissionCombination[] {
  const output: UsersAndGroupsByPermissionCombination[] = [];

  const userPerms = assetPerms.filter((x) => x.subjectType == PermissionSubjectType.User);
  const userIds = userPerms.map((userPerm) => userPerm.subjectId).filter(Distinct);

  userIds.forEach((userId) => {
    const userPermsByMode: PermissionByMode[] = userPerms
      .filter((perm) => perm.subjectId === userId)
      .map((perm) => ({ permission: perm.type, mode: perm.mode }));

    const userPermsCombination = userPermsByMode.sort((a, b) => a.permission.localeCompare(b.permission));
    const userPermsCombinationIndex = output.findIndex((item) =>
      CombinationEquals(item.permissionCombination, userPermsCombination)
    );

    const user = userInfos.find((user) => user.id === userId);
    if (!user) return;

    if (userPermsCombinationIndex === -1) {
      output.push({
        permissionCombination: userPermsCombination,
        users: [user],
        groups: [],
      });
    } else {
      output[userPermsCombinationIndex].users.push(user!);
    }
  });

  const groupPerms = assetPerms.filter((x) => x.subjectType == PermissionSubjectType.Group);
  const groupIds = groupPerms.map((groupPerm) => groupPerm.subjectId).filter(Distinct);

  groupIds.forEach((groupId) => {
    const groupPermsByMode: PermissionByMode[] = groupPerms
      .filter((perm) => perm.subjectId === groupId)
      .map((perm) => ({ permission: perm.type, mode: perm.mode }));

    const groupPermsCombination = groupPermsByMode.sort((a, b) => a.permission.localeCompare(b.permission));
    const groupPermsCombinationIndex = output.findIndex((item) =>
      CombinationEquals(item.permissionCombination, groupPermsCombination)
    );

    const group = groupInfos.find((group) => group.id === groupId);
    if (!group) return;
    if (groupPermsCombinationIndex === -1) {
      output.push({
        permissionCombination: groupPermsCombination,
        users: [],
        groups: [group],
      });
    } else {
      output[groupPermsCombinationIndex].groups.push(group!);
    }
  });

  return output;
}

// #endregion types, interfaces and functions
